import React, { useState, useMemo } from "react";
import {
  Star,
  Calendar,
  Filter,
  Search,
  Clock,
  DollarSign,
  User,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import rawProjectsData from "../data/projects.json";
import {
  sanitizeProjects,
  calculateAverageRating,
  safeParseRating,
} from "../utils/portfolioParser";

const projectsData = sanitizeProjects(rawProjectsData as any[]);

export default function Portfolio() {
  const [yearFilter, setYearFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedReviews, setExpandedReviews] = useState<Set<string>>(
    new Set()
  );

  const toggleReview = (id: string) => {
    setExpandedReviews((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const availableYears = useMemo(() => {
    const years = Array.from(new Set(projectsData.map((p) => p.dates.year)));
    return years.sort((a, b) => b - a);
  }, []);

  const stats = useMemo(() => {
    const total = projectsData.length;
    const avgRating = calculateAverageRating(projectsData);
    const minYear = Math.min(...availableYears);
    const maxYear = Math.max(...availableYears);
    return { total, avgRating, minYear, maxYear };
  }, [availableYears]);

  const filteredProjects = useMemo(() => {
    return projectsData.filter((p) => {
      if (yearFilter !== "all" && p.dates.year !== Number(yearFilter))
        return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesTitle = p.title.toLowerCase().includes(q);
        const matchesSkills = p.skills.some((s) =>
          s.toLowerCase().includes(q)
        );
        if (!matchesTitle && !matchesSkills) return false;
      }
      return true;
    });
  }, [yearFilter, searchQuery]);

  const groupedByYear = useMemo(() => {
    const groups: Record<number, typeof filteredProjects> = {};
    filteredProjects.forEach((p) => {
      const year = p.dates.year;
      if (!groups[year]) groups[year] = [];
      groups[year].push(p);
    });
    return Object.keys(groups)
      .map(Number)
      .sort((a, b) => b - a)
      .map((year) => ({ year, projects: groups[year] }));
  }, [filteredProjects]);

  const renderStars = (rating: number) => {
    const safe = safeParseRating(rating);
    return (
      <span className="inline-flex gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            size={14}
            className={
              i <= safe ? "fill-amber-400 text-amber-400" : "text-slate-600"
            }
          />
        ))}
      </span>
    );
  };

  return (
    <div className="pt-[72px]">
      {/* Header */}
      <section className="relative overflow-hidden border-b border-white/5 bg-slate-950 py-20">
        <div className="animate-gradient-bg absolute inset-0 bg-gradient-to-br from-slate-950 via-indigo-950/30 to-slate-950 opacity-60" />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <h1 className="text-4xl font-extrabold text-white sm:text-5xl">
            Our Portfolio
          </h1>
          <p className="mt-4 text-lg text-slate-400">
            A timeline of projects we&rsquo;ve delivered since 2012. Over{" "}
            {stats.total} projects completed with an average {stats.avgRating}★
            rating.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="border-b border-white/5 bg-slate-900/50 py-8">
        <div className="mx-auto flex max-w-3xl flex-wrap justify-center gap-12 px-6">
          <div className="flex flex-col items-center gap-1">
            <Calendar className="text-cyan-400" size={22} />
            <span className="text-xl font-bold text-white">
              {stats.minYear}&ndash;{stats.maxYear}
            </span>
            <span className="text-xs text-slate-500">Year Range</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Star className="text-amber-400" size={22} />
            <span className="text-xl font-bold text-white">
              {stats.avgRating}
            </span>
            <span className="text-xs text-slate-500">Avg Rating</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <User className="text-purple-400" size={22} />
            <span className="text-xl font-bold text-white">
              {stats.total}+
            </span>
            <span className="text-xs text-slate-500">Projects Completed</span>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-white/5 bg-slate-900/30 py-5">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-4 px-6">
          <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-slate-800/60 px-3 py-2">
            <Filter size={16} className="text-slate-500" />
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="bg-transparent text-sm text-slate-300 outline-none"
              aria-label="Filter by year"
            >
              <option value="all">All Years</option>
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-slate-800/60 px-3 py-2">
            <Search size={16} className="text-slate-500" />
            <input
              type="text"
              placeholder="Search by title or skill…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-48 bg-transparent text-sm text-slate-300 placeholder-slate-600 outline-none"
              aria-label="Search projects"
            />
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-6">
          {groupedByYear.map(({ year, projects }) => (
            <div key={year} className="relative">
              {/* Year marker */}
              <div className="sticky top-[72px] z-10 mb-6 flex items-center gap-3">
                <span className="rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 px-4 py-1.5 text-sm font-bold text-white shadow">
                  {year}
                </span>
                <div className="h-px flex-1 bg-white/10" />
              </div>

              {/* Projects */}
              <div className="relative mb-12 border-l-2 border-white/10 pl-8">
                {projects.map((project) => (
                  <div key={project.id} className="group relative mb-8 last:mb-0">
                    {/* Dot on timeline */}
                    <div className="absolute -left-[41px] top-2 h-3 w-3 rounded-full border-2 border-cyan-500 bg-slate-950" />

                    <div className="rounded-2xl border border-white/5 bg-slate-900/60 p-6 transition-all hover:border-cyan-500/20 hover:shadow-md hover:shadow-cyan-500/5">
                      <h3 className="text-lg font-semibold text-white">
                        {project.title}
                      </h3>
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                        <Calendar size={12} /> {project.dates.postedDisplay}
                      </p>

                      {project.description && (
                        <p className="mt-3 text-sm leading-relaxed text-slate-400">
                          {project.description.length > 150
                            ? project.description.slice(0, 150) + "…"
                            : project.description}
                        </p>
                      )}

                      {/* Skills badges */}
                      {project.skills.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {project.skills.map((skill) => (
                            <span
                              key={skill}
                              className="rounded-full border border-white/10 bg-slate-800/80 px-3 py-1 text-xs font-medium text-slate-300"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Review */}
                      {project.review && project.review.rating > 0 && (
                        <div className="mt-5 rounded-xl border border-white/5 bg-slate-800/40 p-4">
                          <div className="flex items-center gap-2">
                            {renderStars(project.review.rating)}
                            <span className="text-xs text-slate-500">
                              {project.review.rating.toFixed(1)}
                            </span>
                          </div>
                          <p className="mt-2 text-sm italic text-slate-400">
                            {project.review.text.length > 200 &&
                            !expandedReviews.has(project.id)
                              ? `"${project.review.text.slice(0, 200)}…"`
                              : `"${project.review.text}"`}
                          </p>
                          {project.review.text.length > 200 && (
                            <button
                              onClick={() => toggleReview(project.id)}
                              className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-cyan-400 hover:text-cyan-300"
                            >
                              {expandedReviews.has(project.id) ? (
                                <>
                                  Show Less <ChevronUp size={12} />
                                </>
                              ) : (
                                <>
                                  Read More <ChevronDown size={12} />
                                </>
                              )}
                            </button>
                          )}
                          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                            <span className="inline-flex items-center gap-1">
                              <User size={12} />{" "}
                              {project.review.clientName}
                            </span>
                            {project.review.onTime === "YES" && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-emerald-400">
                                <Clock size={10} /> On Time
                              </span>
                            )}
                            {project.review.onBudget === "YES" && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-blue-400">
                                <DollarSign size={10} /> On Budget
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {filteredProjects.length === 0 && (
            <p className="py-20 text-center text-slate-500">
              No projects match your filters.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
